import { Component, AfterViewInit, Input, Output, EventEmitter, QueryList, ViewChildren, ViewChild, ElementRef } from '@angular/core';
import { NewsroomSideBarModel } from '../../models/newsroom-side-bar.model';
import { NewsroomSideBarItemModel } from '../../models/newsroom-side-bar-item.model';
import { StoriesConstants } from '../../../Newsroom/components/stories/stories-constants';
import { FilterService, LazyLoadEvent } from 'primeng/api';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { canDeactivateGuardConstants } from '../../../Newsroom/services/can-deactivate-guard.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { KebabMenuComponent } from '../../../../Common/Forms/components/KebabMenu/kebab-menu.component';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { NewsroomLeftSidebarService } from '../../../Newsroom/services/newsroom-left-sidebar.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';

@Component({
    selector: 'agpr-newsroom-left-sidebar',
    templateUrl: './newsroom-left-sidebar.component.html',
    styleUrls: ['./newsroom-left-sidebar.component.scss'],
    providers: [
        {
            provide: 'SortOrderServiceInterface',
            useExisting: NewsroomLeftSidebarService
        },
        {
            provide: FilterService,
            useExisting: NewsroomLeftSidebarService
        }
    ],
    standalone: false
})
export class NewsroomLeftSidebarComponent implements AfterViewInit {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    @Input() sidebarModel: NewsroomSideBarModel = new NewsroomSideBarModel();
    @Input() totalStories: number = 0;
    @Input() start: number = 0;
    @Input() displayRows: number = 10;
    @Input() storyLoading: boolean = false;
    @Input() scrollHeight: number;
    @Input() selectedItem: NewsroomSideBarItemModel = new NewsroomSideBarItemModel();
    @Input() noItemsText: string;
    @Input() itemLabel: string;
    @Input() statusLabel: string = 'Status';
    @Input() amplifyButtonLabel: string;
    @Input() sections: DropdownOptionModel[];
    @Input() selectedSectionValue: DropdownOptionModel;
    @Output() lazyLoad: EventEmitter<LazyLoadEvent> = new EventEmitter<LazyLoadEvent>();
    @Output() startChange: EventEmitter<number> = new EventEmitter<number>();
    @Output() selectionChange: EventEmitter<number> = new EventEmitter<number>();
    @Output() showAmplifyDialog: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() deleteItem: EventEmitter<NewsroomSideBarItemModel> = new EventEmitter<NewsroomSideBarItemModel>();
    @Output() sectionChanged: EventEmitter<any> = new EventEmitter<any>();
    @Input() isFormDirty: boolean;
    public lazyLoadEvent: LazyLoadEvent = null;
    public collapseSidebar = false;
    public collapseList = false;
    public showListPanel = false;
    private previousSelectedSectionValue: DropdownOptionModel;

    @ViewChildren('kebabMenu') kebabMenus: QueryList<KebabMenuComponent>;
    @ViewChild('storyList') storyList: ElementRef;

    leftSidebarCollapseBtnIcon: string = 'fa-solid fa-circle-chevron-left';

    constructor(
        private coreDialogService: CoreDialogService
    ) { }

    ngAfterViewInit() {
        if (this.lazyLoadEvent) {
            this.lazyLoadEvent.first = this.start;
            this.lazyLoadEvent.rows = this.displayRows;
        }

        this.previousSelectedSectionValue = this.selectedSectionValue;
    }

    scrolledToBottom(): void {
        if (this.lazyLoadEvent && (this.start < this.totalStories)) {
            this.start = this.start + this.lazyLoadEvent.rows;
            this.lazyLoadEvent.first  = this.start ;
            this.startChange.emit(this.start);
            this.fetchStories(this.lazyLoadEvent);
        }
    }

    loadPreviewPanel(item: NewsroomSideBarItemModel) {
        this.selectedItem.id = 0;
        this.selectionChange.emit(item.id);
    }

    getContentSnippet(content: string): string {
        if (content.length > StoriesConstants.maxNumberOfChars) {
            content = content.slice(0, StoriesConstants.maxNumberOfChars);
            const spacePosition = content.lastIndexOf(' ');
            if (spacePosition !== -1) {
                content = content.slice(0, spacePosition);
            }
            content = content + StoriesConstants.ellipses;
        }
        return content;
    }

    // This is triggered when wire type is selected from drop down and in turn navigates to the relavant feature
    selectedSectionChanged(): void {
        if (this.isFormDirty) {
            this.coreDialogService.open(
                ConfirmDialogComponent,
                {
                    inputs: {
                        heading: canDeactivateGuardConstants.confirmHeading,
                        messageHtml: canDeactivateGuardConstants.confirmMessage,
                        okLabel: canDeactivateGuardConstants.continueLabel,
                        cancelLabel: canDeactivateGuardConstants.cancelLabel
                    },
                    outputs: {
                        dialogClose: (confirmed: boolean) => {
                            if (confirmed) {
                                this.previousSelectedSectionValue = this.selectedSectionValue ;
                                this.sectionChanged.emit(this.selectedSectionValue.value);
                            } else {
                                this.selectedSectionValue = this.previousSelectedSectionValue;
                            }
                        }
                    }
                }
            );
        } else {
            this.sectionChanged.emit(this.selectedSectionValue.value);
        }
    }

    public showAmplify(): void {
        this.showAmplifyDialog.emit();
    }

    public deleteStory(event: any, item: NewsroomSideBarItemModel) {
        event.stopPropagation();
        this.deleteItem.emit(item);
    }

    public fetchStories(event: LazyLoadEvent) {
        this.lazyLoad.emit(event);
    }

    hideKebabMenu(event){
        const hoverKebabId = event?.target?.querySelector('agpr-kebab-menu')?.getAttribute('data-id');
        this.kebabMenus.forEach(element => {
            if (hoverKebabId !== element.id) {
                element.hide();
            }
        });
    }

    // Toogle icon classes after click on collapse button
    collapseLeftSidebar(): void {
        this.collapseSidebar = !this.collapseSidebar;

        if(this.collapseSidebar) {
            this.leftSidebarCollapseBtnIcon = 'fa-solid fa-circle-chevron-right';
        } else {
            this.leftSidebarCollapseBtnIcon = 'fa-solid fa-circle-chevron-left';
        }
    }
}
